import {
  type BonusField,
  type ChestPrize,
  PRIZE_WEIGHT,
  shieldedRefs,
  shieldToken,
  takeBonus,
} from '@/shared/engine/bonus'
import { applyMove, type GameState, hasWon, withShields } from '@/shared/engine/state'
import { playTurn, settleTurn, type Turn, withExtraRoll } from '@/shared/engine/turn'
import type { Move } from '@/shared/game/types'
import type { Brain, Seen } from '../match'
import { plansFor, sendable } from '../plans'
import { encode, type Sparse } from './features'
import { forward, type Model, type Pass, SEATS } from './model'

/**
 * Голова на сети: перебрать замыслы на этот бросок, доиграть каждый на
 * копии позиции и спросить сеть, насколько хорошо стало.
 *
 * Замыслы — те же, что у эвристики (plansFor): пары ходов на оба кубика или
 * одиночные, если второй не играется. Оценивается позиция ПОСЛЕ замысла
 * целиком, с подбором бонусов и начисленными добросами, — сеть учили именно
 * на таких позициях.
 */

/** Замысел и во что он превращает позицию. */
export type Option = {
  plan: Move[]
  /** Мой шанс на победу после замысла, 0..1. */
  value: number
  /** Самая вероятная позиция после замысла — её обучение и двигает. */
  x: Sparse
  pass: Pass
}

/** Чем кончился замысел: позиция и вероятность такого исхода (сундук). */
type Outcome = { state: GameState; field: BonusField | null; turn: Turn; weight: number }

/** Сундук за одним устройством: доброс или щит, монеты не выпадают (local/store). */
const CHEST: [ChestPrize, number][] = (() => {
  const total = PRIZE_WEIGHT.roll + PRIZE_WEIGHT.shield

  return [
    ['roll', PRIZE_WEIGHT.roll / total],
    ['shield', PRIZE_WEIGHT.shield / total],
  ]
})()

/** Победа в замысле: оценивать нечего. */
const WON = 1

export function evaluate(model: Model, seen: Seen): Option[] {
  const me = seen.turn.player
  const present = seen.state.players.length

  return plansFor(seen.state, me, [...seen.turn.dice]).map((plan) => {
    const outcomes = play(seen, plan)
    let value = 0
    let best: { x: Sparse; pass: Pass; weight: number } | null = null

    for (const outcome of outcomes) {
      if (hasWon(outcome.state, me)) {
        value += WON * outcome.weight
        continue
      }

      const settled = settleTurn(outcome.turn, outcome.state)
      const pending = settled.finished ? 0 : Math.min(settled.bonus, 2)
      const x = encode(outcome.state, outcome.field, me, pending)
      const pass = forward(model, x, present)

      value += pass.odds[0] * outcome.weight

      if (best === null || outcome.weight > best.weight) best = { x, pass, weight: outcome.weight }
    }

    // Выигрышный замысел в обучение не идёт: учить сеть тому, что победа —
    // это победа, незачем, это знает и перебор.
    const won = best ?? { x: { index: [], value: [] }, pass: certain(), weight: 1 }

    return { plan, value, x: won.x, pass: won.pass }
  })
}

/**
 * Голова для партии.
 *
 * @param temperature насколько небрежно выбирать: 0 — всегда лучший замысел,
 *   больше — чаще второй-третий по оценке. Разница шансов меряется в долях
 *   (0.05 — это пять процентов победы), и при temperature 0.05 замысел,
 *   который хуже лучшего на 5%, выбирается в e раз реже него.
 * @param onChoice обучение подслушивает здесь, что выбрано и как оценено.
 */
export function netBrain(
  model: Model,
  temperature: number,
  random: () => number,
  onChoice?: (chosen: Option, seen: Seen) => void,
): Brain {
  return (seen) => {
    const options = evaluate(model, seen)

    if (options.length === 0) return []

    const chosen = pick(options, temperature, random)

    onChoice?.(chosen, seen)

    return sendable(chosen.plan)
  }
}

function pick(options: Option[], temperature: number, random: () => number): Option {
  let top = Number.NEGATIVE_INFINITY

  for (const option of options) top = Math.max(top, option.value)

  if (temperature <= 0) {
    // Равные по оценке — жребием, как у эвристики: иначе бот при одинаковой
    // позиции всегда двигал бы одну и ту же фишку.
    const best = options.filter((option) => option.value === top)

    return best[Math.floor(random() * best.length)]
  }

  const weights = options.map((option) => Math.exp((option.value - top) / temperature))
  let roll = random() * weights.reduce((sum, w) => sum + w, 0)

  for (const [at, option] of options.entries()) {
    roll -= weights[at]

    if (roll <= 0) return option
  }

  return options[options.length - 1]
}

/**
 * Доиграть замысел на копии: ходы, подбор бонусов на каждом шаге, как в
 * партии. Сундук ветвит исход — приз в нём случаен.
 */
function play(seen: Seen, plan: Move[]): Outcome[] {
  let outcomes: Outcome[] = [{ state: seen.state, field: seen.field, turn: seen.turn, weight: 1 }]

  for (const move of plan) {
    outcomes = outcomes.flatMap((outcome) => {
      if (hasWon(outcome.state, move.color)) return [outcome]

      const state = applyMove(outcome.state, move)
      const turn = playTurn(outcome.turn, move)

      return pickUp({ ...outcome, state, turn }, move)
    })
  }

  return outcomes
}

function pickUp(outcome: Outcome, move: Move): Outcome[] {
  if (outcome.field === null || move.to.location !== 'ring') return [outcome]

  const [taken, kind] = takeBonus(outcome.field, move.to.index)

  if (kind === null) return [outcome]

  const prizes: [ChestPrize | null, number][] = kind === 'chest' ? CHEST : [[null, 1]]

  return prizes.map(([prize, weight]) => {
    let field = taken
    let turn = outcome.turn

    if (kind === 'roll' || prize === 'roll') turn = withExtraRoll(turn)
    if (kind === 'shield' || prize === 'shield') {
      field = shieldToken(field, { color: move.color, index: move.token })
    }

    return {
      state: withShields(outcome.state, shieldedRefs(field)),
      field,
      turn,
      weight: outcome.weight * weight,
    }
  })
}

function certain(): Pass {
  const odds = new Float64Array(SEATS)

  odds[0] = 1

  return { hidden: new Float64Array(0), odds }
}
